import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { HStack, Loader } from "@zfp-akademie-a-s/zfp-design";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { FormDrawer } from "Components/Analyses/_Shared/FormDrawer/FormDrawer";
import { FormDrawerAddTile } from "Components/Analyses/_Shared/FormDrawer/FormDrawerAddTile";
import { FormDrawerTiles } from "Components/Analyses/_Shared/FormDrawer/FormDrawerTiles";
import { toPersonPickerPersons } from "Components/_Shared/Inputs/PersonPicker/personPickerPersons";
import { type IncomeBulkFormType, type IncomeDraft, getIncomeBulkFormSchema } from "Forms/Schemas/Budget/Incomes/incomeBulkFormSchema";
import {
  getGetBudgetOverviewQueryKey,
  getListIncomesQueryKey,
  useGetAnalysisClients,
  useListIncomes,
  useSaveIncomesBatch,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { type BudgetBatchBadRequestError, BudgetBatchItemErrorCode, IncomeIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPlannedChild } from "Infrastructure/Utils/ClientUtils";
import { formatItemsCount } from "Infrastructure/Utils/LanguageUtils";
import { type FC, useContext, useEffect, useRef, useState } from "react";
import { type FieldErrors, FormProvider, useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  type IncomesBatch,
  createIncomeDraft,
  getChangedIncomeTypes,
  getSavedIncomeDrafts,
  locateIncomesBatchErrors,
  toIncomeDrafts,
  toIncomesBatch,
} from "../incomeDrafts";
import { INCOME_TYPE_ICON, INCOME_TYPE_ORDER, INCOME_TYPE_TRANSLATION_KEY } from "../incomeTypes";
import { IncomeDraftCard } from "./IncomeDraftCard";

type Props = {
  readonly open: boolean;
  /** The drawer opens on this income's type with its card in view. */
  readonly initialIncomeGuid?: string;
  readonly onClose: () => void;
};

const FIRST_INCOME_TYPE = IncomeIncomeType.employment;

const Errors = Resources.AnalysisDetail.Budget.Incomes.Form.Errors;

export const IncomeBulkDrawer: FC<Props> = ({ open, initialIncomeGuid, onClose }) => {
  const { t } = useResource();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);
  const queryClient = useQueryClient();
  const contentRef = useRef<HTMLDivElement>(null);
  const isInitializedRef = useRef(false);
  const [activeIncomeType, setActiveIncomeType] = useState<IncomeIncomeType>(FIRST_INCOME_TYPE);
  const [deletedGuids, setDeletedGuids] = useState<string[]>([]);
  const [scrollToErrorRequest, setScrollToErrorRequest] = useState(0);
  const [revealedIncomeGuid, setRevealedIncomeGuid] = useState<string | null>(null);
  // what the drafts are compared against - captured on opening, so a refetch doesn't turn untouched cards into changes
  const [savedDrafts, setSavedDrafts] = useState<ReadonlyMap<string, IncomeDraft>>(() => new Map());

  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);
  const { data: incomes } = useListIncomes(analysisId, versionId);
  const { mutateAsync: saveIncomesBatch, isPending: isSaving } = useSaveIncomesBatch();

  const methods = useForm<IncomeBulkFormType>({
    resolver: yupResolver(getIncomeBulkFormSchema(t)),
    defaultValues: { drafts: [] },
  });
  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: "drafts" });

  useEffect(() => {
    if (!open) {
      isInitializedRef.current = false;
      return;
    }
    // incomes refetched while the drawer is open (e.g. after a failed save) must not wipe the edits
    if (isInitializedRef.current || !incomes) {
      return;
    }
    isInitializedRef.current = true;
    reset({ drafts: incomes.flatMap(toIncomeDrafts) });
    setSavedDrafts(getSavedIncomeDrafts(incomes));
    setDeletedGuids([]);
    const initialIncome = incomes.find((income) => income.guid === initialIncomeGuid);
    setActiveIncomeType(initialIncome?.incomeType ?? FIRST_INCOME_TYPE);
    setRevealedIncomeGuid(initialIncome?.guid ?? null);
  }, [open, incomes, initialIncomeGuid, reset]);

  useEffect(() => {
    // waits until the reset form has the income's card
    if (!revealedIncomeGuid || !fields.some((field) => field.guid === revealedIncomeGuid)) {
      return;
    }
    setRevealedIncomeGuid(null);
    const card = contentRef.current?.querySelector<HTMLElement>(`[data-income-guid="${revealedIncomeGuid}"]`);
    card?.scrollIntoView({ block: "center" });
    card?.querySelector<HTMLElement>('input:not([type="radio"]):not(:disabled)')?.focus({ preventScroll: true });
  }, [revealedIncomeGuid, fields]);

  useEffect(() => {
    if (scrollToErrorRequest === 0) {
      return;
    }
    const card = contentRef.current?.querySelector<HTMLElement>('[data-invalid="true"]');
    card?.scrollIntoView({ behavior: "smooth", block: "center" });
    card?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus({ preventScroll: true });
  }, [scrollToErrorRequest]);

  const drafts = watch("drafts");
  const changedIncomeTypes = getChangedIncomeTypes(drafts, savedDrafts, deletedGuids);
  const unsavedCount = changedIncomeTypes.length;
  const activeCount = fields.filter((field) => field.incomeType === activeIncomeType).length;
  const activeIncomeTypeLabel = t(Resources.AnalysisDetail.Budget.Incomes.IncomeTypes[INCOME_TYPE_TRANSLATION_KEY[activeIncomeType]]);

  const persons = toPersonPickerPersons((clientsData?.clients ?? []).filter((client) => !isPlannedChild(client)));

  // with a single person to choose from there's nothing to pick
  const newDraftClientGuid = persons.length === 1 ? persons[0]?.value : undefined;
  const addDraft = () => append(createIncomeDraft(activeIncomeType, newDraftClientGuid), { shouldFocus: false });

  // a saved income is only deleted on save, until then it just disappears from the drawer;
  // one that someone else has deleted in the meantime is no longer in the current list and has nothing to delete
  const removeDraft = (index: number) => {
    const guid = drafts[index]?.guid;
    if (guid && incomes?.some((income) => income.guid === guid)) {
      setDeletedGuids((guids) => [...guids, guid]);
    }
    remove(index);
  };

  const revealErrors = (erroredIncomeTypes: IncomeIncomeType[]) => {
    const [firstErroredIncomeType] = erroredIncomeTypes;
    if (firstErroredIncomeType && !erroredIncomeTypes.includes(activeIncomeType)) {
      setActiveIncomeType(firstErroredIncomeType);
    }
    setScrollToErrorRequest((request) => request + 1);
  };

  const onInvalid = (formErrors: FieldErrors<IncomeBulkFormType>) => {
    revealErrors(fields.filter((_, index) => formErrors.drafts?.[index]).map((field) => field.incomeType));
  };

  // the batch is all or nothing, so on an error nothing is saved and the errors only lead back to the cards
  const showSaveErrors = (error: unknown, batch: IncomesBatch, savedValues: IncomeBulkFormType) => {
    const itemErrors = locateIncomesBatchErrors((error as Partial<BudgetBatchBadRequestError> | null)?.errors ?? [], batch);
    const isDataChanged = itemErrors.some(({ code }) => code === BudgetBatchItemErrorCode.income_not_found);
    const erroredIncomeTypes: IncomeIncomeType[] = [];

    for (const itemError of itemErrors) {
      if (itemError.kind === "delete") {
        continue;
      }
      const incomeType = savedValues.drafts[itemError.index]?.incomeType;
      if (incomeType) {
        erroredIncomeTypes.push(incomeType);
      }
      if (itemError.code === BudgetBatchItemErrorCode.client_not_belong_to_analysis) {
        setError(`drafts.${itemError.index}.clientGuid`, { type: "server", message: t(Errors.ClientNotInAnalysis) });
      } else {
        setError(`drafts.${itemError.index}`, {
          type: "server",
          message: t(itemError.code === BudgetBatchItemErrorCode.income_not_found ? Errors.IncomeNotFound : Errors.IncomeSubmitError),
        });
      }
    }

    if (isDataChanged) {
      // an income deleted in the meantime can't be deleted again, and with the fresh list removing its card won't try to
      const goneGuids = itemErrors.flatMap((itemError) => (itemError.kind === "delete" ? [itemError.guid] : []));
      setDeletedGuids((guids) => guids.filter((guid) => !goneGuids.includes(guid)));
      queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
    }

    toast.error(t(isDataChanged ? Errors.DataChanged : Errors.IncomeSubmitError));
    if (erroredIncomeTypes.length > 0) {
      revealErrors(erroredIncomeTypes);
    }
  };

  const onValid = async (data: IncomeBulkFormType) => {
    const batch = toIncomesBatch(data.drafts, savedDrafts, deletedGuids);

    try {
      await saveIncomesBatch({ analysisId, versionId, data: batch.body });
    } catch (error) {
      showSaveErrors(error, batch, data);
      return;
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) }),
      queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) }),
    ]);
    onClose();
  };

  return (
    <FormProvider {...methods}>
      <FormDrawer
        open={open}
        size="lg"
        title={t(Resources.AnalysisDetail.Budget.Incomes.Title)}
        submitLabel={t(Resources.Common.SaveAndClose)}
        onClose={onClose}
        onSubmit={handleSubmit(onValid, onInvalid)}
        isSubmitting={isSubmitting || isSaving}
        isSubmitDisabled={readOnly || unsavedCount === 0}
        unsavedCount={unsavedCount}
        navigation={
          <FormDrawerTiles
            label={t(Resources.AnalysisDetail.Budget.Incomes.IncomeType)}
            tiles={INCOME_TYPE_ORDER.map((incomeType) => {
              const indexes = fields.flatMap((field, index) => (field.incomeType === incomeType ? [index] : []));

              return {
                key: incomeType,
                icon: INCOME_TYPE_ICON[incomeType],
                label: t(Resources.AnalysisDetail.Budget.Incomes.IncomeTypes[INCOME_TYPE_TRANSLATION_KEY[incomeType]]),
                count: indexes.length,
                hasError: indexes.some((index) => !!errors.drafts?.[index]),
              };
            })}
            activeKey={activeIncomeType}
            onSelect={setActiveIncomeType}
          />
        }
      >
        {incomes ? (
          <Stack ref={contentRef} spacing={Spacing.L}>
            <HStack spacing={Spacing.M} justifyContent="space-between" alignItems="baseline">
              <Typography variant="h3" component="h3">
                {activeIncomeTypeLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(Resources.Common.Total)}{" "}
                <Box component="span" fontWeight={700} color="text.primary">
                  {formatItemsCount(activeCount, t)}
                </Box>
              </Typography>
            </HStack>

            <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={Spacing.L} alignItems="start">
              {fields.map(
                (field, index) =>
                  field.incomeType === activeIncomeType && (
                    <IncomeDraftCard
                      key={field.id}
                      index={index}
                      guid={field.guid}
                      incomeType={field.incomeType}
                      persons={persons}
                      isReadOnly={!!drafts[index]?.isReadOnly}
                      onRemove={() => removeDraft(index)}
                    />
                  ),
              )}

              <FormDrawerAddTile
                title={t(
                  activeCount > 0 ? Resources.AnalysisDetail.Budget.Incomes.AddAnotherIncome : Resources.AnalysisDetail.Budget.Incomes.Add,
                )}
                subtitle={activeIncomeTypeLabel}
                disabled={readOnly}
                onClick={addDraft}
              />
            </Box>
          </Stack>
        ) : (
          <Loader />
        )}
      </FormDrawer>
    </FormProvider>
  );
};
